"use client";

import { c as _c } from "react/compiler-runtime";
import type { Editor } from "@tiptap/core";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import dynamic from "next/dynamic";
import NoteAttachmentSection from "@/app/[tenant]/(console)/scribe/playground/[id]/note-attachment-section";
import NoteDocumentsSection from "@/app/[tenant]/(console)/scribe/playground/[id]/note-documents-section";
import PatientReportedMedicationInput from "@/components/patient-reported-medication-input";
import { MeasurementSourceBadge } from "@/components/patient/measurements/measurement-source-badge";
import { ProviderSignatureInput } from "@/components/provider/provider-signature-input";
import { Icd10CodesInput } from "@/components/scribe/icd10-codes-answer";
import TiptapEditor from "@/components/tiptap/editor";
import { Button, Input, Label } from "@/components/ui";
import { Checkbox } from "@/components/ui/checkbox";
import { DatePickerWithYearDropdown } from "@/components/ui/date-picker";
import HeightInput from "@/components/ui/height-input";
import { MultiSelect, MultiSelectContent, MultiSelectItem, MultiSelectPills, MultiSelectTrigger } from "@/components/ui/multi-select";
import MultiTagInput from "@/components/ui/multi-tag";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectTrigger, SelectValue } from "@/components/ui/select";
import DeselectableSelectItem from "@/components/ui/select/deselectable-select-item";
import Switch from "@/components/ui/switch";
import { TimeInput } from "@/components/ui/time-input";
import { useOptionalScribeSuggestionsActions } from "@/contexts/scribe-suggestions-provider";
import { useUser } from "@/contexts/user-provider";
import { clinicClockFor, pickedDay } from "@/lib/clinic-clock";
import { fromDateOnlyString, joinDateAnswer, splitDateAnswer } from "@/lib/format-data";
import { canClearAnswer, nextToggleValue } from "@/lib/forms/deselect";
import { formatOptionValue, optionDisplay, optionLabelsByValue } from "@/lib/forms/option-display";
import { isIcd10CodesQuestion, readIcd10Answer, toIcd10AnswerData } from "@/lib/icd10-codes-field";
import { isPatientReportedMedicationQuestion, serializePatientReportedMedications, toPatientReportedMedicationEntries } from "@/lib/patient-reported-medications";
import { cx } from "@/lib/tailwind-utils";
import { useStoreTimezone } from "@/lib/timezone";
import { Date as DateQuestion, DentalChartAnswer, DentalChart as DentalChartConfig, Disclaimer, GFEClearance, MultiSelect as MultiSelectType, Number, OcclusionExamAnswer, OcclusionExam as OcclusionExamConfig, PerioChartAnswer, PerioChart as PerioChartConfig, ProviderSignature as ProviderSignatureConfig, QuestionData, QuestionType, Rebooking, Signature as SignatureConfig, SingleSelect, Statement, SuppliesUsed, Tag, Text } from "@/types/forms/question";
import { NoteAnswerDetail, NoteQuestionDetail } from "@/types/notes";
import { GFEClearanceAnswerEditor } from "./gfe-clearance-answer-editor";
import { MedicationAnswerEditor } from "./medication-answer-editor";
import { RebookingAnswerEditor } from "./rebooking-answer-editor";
import { SoldItemsAnswerEditor } from "./sold-items-answer-editor";
import { SuppliesUsedAnswerEditor } from "./supplies-used-answer-editor";
import { TableAnswerEditor } from "./table-answer-editor";
import { WeightAnswerEditor } from "./weight-answer-editor";
import { useSuggestions } from "@/api/query/provider/suggestions";
const DentalChartLoading = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="h-32 animate-pulse rounded-lg bg-background-secondary/30" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
const DentalChartComponent = dynamic(() => import("@/components/dental/dental-chart"), {
  ssr: false,
  loading: DentalChartLoading
});
const OcclusionExamComponent = dynamic(() => import("@/components/dental/occlusion-exam"), {
  ssr: false,
  loading: DentalChartLoading
});
const PerioChartComponent = dynamic(() => import("@/components/dental/perio-chart"), {
  ssr: false,
  loading: DentalChartLoading
});

/**
 * How one option of a select question reads while a provider fills the note.
 *
 * A question bound to a measurement field carries the clinic's wording for each value,
 * and the note has to read the way the form does — a bare `0` next to `4` says nothing
 * about which end of the scale is which. Without labels this is the plain option, which
 * is every question not bound to a field.
 */
const selectOptionDisplay = (data: {
  options?: string[];
  optionLabels?: string[];
}) => {
  const labelsByValue = optionLabelsByValue(data.options, data.optionLabels);
  return (option: string) => optionDisplay(formatOptionValue(option), labelsByValue?.[option]);
};
interface NoteAnswerEditorProps {
  answer: NoteAnswerDetail;
  question: NoteQuestionDetail;
  onChange: (answerId: string, data: Record<string, any>, noteAttachmentId?: string) => void;
  disabled?: boolean;
  noteId?: string;
  patientId?: string;
  fullHeight?: boolean;
  showPatientReportedPrefill?: boolean;
  /** Optional patient + visit context, used by DENTAL_CHART for the header band. */
  patient?: {
    name?: string;
    age?: number | string;
    avatarUrl?: string;
    externalId?: string | number;
  };
  visitDate?: string;
}

/**
 * The date half of a DATE answer, plus the time half when the question asks for
 * one. A time typed before a date has nowhere to be stored — the answer is keyed
 * off the date — so it is held here until a date arrives rather than locking the
 * field.
 */
const DateAnswerField: React.FC<{
  name: string;
  value: string | null;
  includeTime: boolean;
  showTodayButton: boolean;
  disabled: boolean;
  onChange: (value: string | null) => void;
}> = t0 => {
  const $ = _c(41);
  const {
    name,
    value,
    includeTime,
    showTodayButton,
    disabled,
    onChange
  } = t0;
  const timeZone = useStoreTimezone();
  const {
    date: datePart,
    time: timePart
  } = splitDateAnswer(value, timeZone);
  const [pendingTime, setPendingTime] = useState("");
  const time = timePart || pendingTime;
  const dateValue = datePart ? (() => {
    const parsed = fromDateOnlyString(datePart);
    return isNaN(parsed.getTime()) ? undefined : parsed;
  })() : undefined;
  let t1;
  if ($[0] !== datePart || $[1] !== time) {
    t1 = time ? new Date(`${datePart || "2000-01-01"}T${time}`) : new Date(NaN);
    $[0] = datePart;
    $[1] = time;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const timeValue = t1;
  let t2;
  if ($[3] !== name) {
    t2 = <Label className="text-body-strong">{name}</Label>;
    $[3] = name;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== onChange || $[6] !== setPendingTime || $[7] !== time || $[8] !== timeZone) {
    t3 = date => {
      onChange(date ? joinDateAnswer(pickedDay(date), time, timeZone) : null);
      setPendingTime("");
    };
    $[5] = onChange;
    $[6] = setPendingTime;
    $[7] = time;
    $[8] = timeZone;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== dateValue || $[11] !== t3) {
    t4 = <div className="w-52 shrink-0"><DatePickerWithYearDropdown value={dateValue} onChange={t3} className="w-full" /></div>;
    $[10] = dateValue;
    $[11] = t3;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  let t5;
  if ($[13] !== datePart || $[14] !== disabled || $[15] !== includeTime || $[16] !== name || $[17] !== onChange || $[18] !== setPendingTime || $[19] !== timeValue || $[20] !== timeZone) {
    t5 = includeTime && <div className="w-40 shrink-0"><TimeInput date={timeValue} ariaLabel={`${name} time`} placeholder="hh:mm AM/PM" disabled={disabled} className="w-full" onClear={() => {
        setPendingTime("");
        if (datePart) {
          onChange(joinDateAnswer(datePart, "", timeZone));
        }
      }} onChange={picked => {
        const hhmm = `${String(picked.getHours()).padStart(2, "0")}:${String(picked.getMinutes()).padStart(2, "0")}`;
        if (datePart) {
          onChange(joinDateAnswer(datePart, hhmm, timeZone));
        } else {
          setPendingTime(hhmm);
        }
      }} /></div>;
    $[13] = datePart;
    $[14] = disabled;
    $[15] = includeTime;
    $[16] = name;
    $[17] = onChange;
    $[18] = setPendingTime;
    $[19] = timeValue;
    $[20] = timeZone;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  let t6;
  if ($[22] !== disabled || $[23] !== onChange || $[24] !== setPendingTime || $[25] !== showTodayButton || $[26] !== time || $[27] !== timeZone) {
    t6 = showTodayButton && <Button type="button" variant="secondary" size="sm" disabled={disabled} onClick={() => {
      onChange(joinDateAnswer(clinicClockFor(timeZone).today(), time, timeZone));
      setPendingTime("");
    }}>Today</Button>;
    $[22] = disabled;
    $[23] = onChange;
    $[24] = setPendingTime;
    $[25] = showTodayButton;
    $[26] = time;
    $[27] = timeZone;
    $[28] = t6;
  } else {
    t6 = $[28];
  }
  let t7;
  if ($[29] !== t4 || $[30] !== t5 || $[31] !== t6) {
    t7 = <div className="flex flex-wrap items-center gap-2">{t4}{t5}{t6}</div>;
    $[29] = t4;
    $[30] = t5;
    $[31] = t6;
    $[32] = t7;
  } else {
    t7 = $[32];
  }
  let t8;
  if ($[33] !== datePart || $[34] !== includeTime || $[35] !== pendingTime) {
    t8 = includeTime && !datePart && pendingTime && <p className="text-caption text-foreground-muted">Pick a date to save this time.</p>;
    $[33] = datePart;
    $[34] = includeTime;
    $[35] = pendingTime;
    $[36] = t8;
  } else {
    t8 = $[36];
  }
  let t9;
  if ($[37] !== t2 || $[38] !== t7 || $[39] !== t8) {
    t9 = <>{t2}{t7}{t8}</>;
    $[37] = t2;
    $[38] = t7;
    $[39] = t8;
    $[40] = t9;
  } else {
    t9 = $[40];
  }
  return t9;
};
export const NoteAnswerEditor: React.FC<NoteAnswerEditorProps> = t0 => {
  const $ = _c(475);
  const {
    answer,
    question,
    onChange,
    disabled: t1,
    noteId,
    patientId,
    fullHeight: t2,
    showPatientReportedPrefill: t3,
    patient,
    visitDate
  } = t0;
  const disabled = t1 === undefined ? false : t1;
  const fullHeight = t2 === undefined ? false : t2;
  const showPatientReportedPrefill = t3 === undefined ? false : t3;
  const questionData = question.data as QuestionData;
  const {
    currentUser
  } = useUser();
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = ["PERSONAL", "PATIENT", "SAVED", "TENANT", "NOTE_TEMPLATE"];
    $[0] = t4;
  } else {
    t4 = $[0];
  }
  const t5 = currentUser?.id;
  let t6;
  if ($[1] !== question.templateId || $[2] !== t5) {
    t6 = {
      providerId: t5,
      noteTemplateId: question.templateId
    };
    $[1] = question.templateId;
    $[2] = t5;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const {
    data: t7
  } = useSuggestions(t4, patientId, undefined, t6);
  let t8;
  if ($[4] !== t7) {
    t8 = t7 === undefined ? [] : t7;
    $[4] = t7;
    $[5] = t8;
  } else {
    t8 = $[5];
  }
  const allSuggestions = t8;
  let t9;
  if ($[6] !== allSuggestions || $[7] !== question.id) {
    let t10;
    if ($[9] !== question.id) {
      t10 = s => s.type !== "SAVED" || !s.noteQuestionIds || s.noteQuestionIds.length === 0 || s.noteQuestionIds.includes(question.id);
      $[9] = question.id;
      $[10] = t10;
    } else {
      t10 = $[10];
    }
    t9 = allSuggestions.filter(t10);
    $[6] = allSuggestions;
    $[7] = question.id;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  const suggestions = t9;
  const scribeSuggestionsActions = useOptionalScribeSuggestionsActions();
  const [editor, setEditor] = React.useState(null);
  let t10;
  let t11;
  if ($[11] !== editor || $[12] !== question.id || $[13] !== scribeSuggestionsActions) {
    t10 = () => {
      if (!scribeSuggestionsActions || !editor) {
        return;
      }
      return scribeSuggestionsActions.registerEditor(question.id, editor);
    };
    t11 = [scribeSuggestionsActions, editor, question.id];
    $[11] = editor;
    $[12] = question.id;
    $[13] = scribeSuggestionsActions;
    $[14] = t10;
    $[15] = t11;
  } else {
    t10 = $[14];
    t11 = $[15];
  }
  useEffect(t10, t11);
  let t12;
  if ($[16] !== question.id || $[17] !== scribeSuggestionsActions) {
    t12 = () => {
      scribeSuggestionsActions?.setFocusedQuestionId(question.id);
    };
    $[16] = question.id;
    $[17] = scribeSuggestionsActions;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  const handleEditorFocus = t12;
  let t13;
  if ($[19] !== answer.id || $[20] !== onChange) {
    t13 = (newData, noteAttachmentId) => {
      onChange(answer.id, newData, noteAttachmentId);
    };
    $[19] = answer.id;
    $[20] = onChange;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  const handleChange = t13;
  if (question.questionType === QuestionType.MEDICATIONS) {
    let t14;
    if ($[22] !== answer || $[23] !== disabled || $[24] !== noteId || $[25] !== onChange || $[26] !== patientId) {
      t14 = <MedicationAnswerEditor answer={answer} onChange={onChange} disabled={disabled} noteId={noteId} patientId={patientId} />;
      $[22] = answer;
      $[23] = disabled;
      $[24] = noteId;
      $[25] = onChange;
      $[26] = patientId;
      $[27] = t14;
    } else {
      t14 = $[27];
    }
    return t14;
  }
  if (question.questionType === QuestionType.SOLD_ITEMS) {
    let t14;
    if ($[28] !== onChange) {
      t14 = (answerId, data) => onChange(answerId, data);
      $[28] = onChange;
      $[29] = t14;
    } else {
      t14 = $[29];
    }
    let t15;
    if ($[30] !== answer || $[31] !== disabled || $[32] !== questionData.name || $[33] !== t14) {
      t15 = <SoldItemsAnswerEditor answer={answer} onChange={t14} disabled={disabled} title={questionData.name} />;
      $[30] = answer;
      $[31] = disabled;
      $[32] = questionData.name;
      $[33] = t14;
      $[34] = t15;
    } else {
      t15 = $[34];
    }
    return t15;
  }
  if (question.questionType === QuestionType.SUPPLIES_USED) {
    let t14;
    if ($[35] !== onChange) {
      t14 = (answerId_0, data_0) => onChange(answerId_0, data_0);
      $[35] = onChange;
      $[36] = t14;
    } else {
      t14 = $[36];
    }
    const t15 = question.data as SuppliesUsed;
    let t16;
    if ($[37] !== answer || $[38] !== disabled || $[39] !== noteId || $[40] !== questionData.name || $[41] !== t14 || $[42] !== t15) {
      t16 = <SuppliesUsedAnswerEditor answer={answer} onChange={t14} disabled={disabled} title={questionData.name} questionData={t15} noteId={noteId} />;
      $[37] = answer;
      $[38] = disabled;
      $[39] = noteId;
      $[40] = questionData.name;
      $[41] = t14;
      $[42] = t15;
      $[43] = t16;
    } else {
      t16 = $[43];
    }
    return t16;
  }
  if (question.questionType === QuestionType.REBOOKING) {
    let t14;
    if ($[44] !== onChange) {
      t14 = (answerId_1, data_1) => onChange(answerId_1, data_1);
      $[44] = onChange;
      $[45] = t14;
    } else {
      t14 = $[45];
    }
    const t15 = question.data as Rebooking;
    let t16;
    if ($[46] !== answer || $[47] !== disabled || $[48] !== noteId || $[49] !== questionData.name || $[50] !== t14 || $[51] !== t15) {
      t16 = <RebookingAnswerEditor answer={answer} onChange={t14} disabled={disabled} title={questionData.name} questionData={t15} noteId={noteId} />;
      $[46] = answer;
      $[47] = disabled;
      $[48] = noteId;
      $[49] = questionData.name;
      $[50] = t14;
      $[51] = t15;
      $[52] = t16;
    } else {
      t16 = $[52];
    }
    return t16;
  }
  if (question.questionType === QuestionType.GFE_CLEARANCE) {
    let t14;
    if ($[53] !== onChange) {
      t14 = (answerId_2, data_2) => onChange(answerId_2, data_2);
      $[53] = onChange;
      $[54] = t14;
    } else {
      t14 = $[54];
    }
    const t15 = question.data as GFEClearance;
    let t16;
    if ($[55] !== answer || $[56] !== disabled || $[57] !== noteId || $[58] !== questionData.name || $[59] !== t14 || $[60] !== t15) {
      t16 = <GFEClearanceAnswerEditor answer={answer} onChange={t14} disabled={disabled} title={questionData.name} questionData={t15} noteId={noteId} />;
      $[55] = answer;
      $[56] = disabled;
      $[57] = noteId;
      $[58] = questionData.name;
      $[59] = t14;
      $[60] = t15;
      $[61] = t16;
    } else {
      t16 = $[61];
    }
    return t16;
  }
  if (question.questionType === QuestionType.TABLE) {
    let t14;
    if ($[62] !== onChange) {
      t14 = (answerId_3, data_3) => onChange(answerId_3, data_3);
      $[62] = onChange;
      $[63] = t14;
    } else {
      t14 = $[63];
    }
    let t15;
    if ($[64] !== answer || $[65] !== disabled || $[66] !== question || $[67] !== questionData.name || $[68] !== t14) {
      t15 = <TableAnswerEditor answer={answer} question={question} onChange={t14} disabled={disabled} title={questionData.name} />;
      $[64] = answer;
      $[65] = disabled;
      $[66] = question;
      $[67] = questionData.name;
      $[68] = t14;
      $[69] = t15;
    } else {
      t15 = $[69];
    }
    return t15;
  }
  if (isIcd10CodesQuestion(question)) {
    let t14;
    if ($[70] !== questionData.name) {
      t14 = <Label className="text-body-strong">{questionData.name}</Label>;
      $[70] = questionData.name;
      $[71] = t14;
    } else {
      t14 = $[71];
    }
    let t15;
    if ($[72] !== answer.data) {
      t15 = readIcd10Answer(answer.data);
      $[72] = answer.data;
      $[73] = t15;
    } else {
      t15 = $[73];
    }
    let t16;
    if ($[74] !== handleChange) {
      t16 = next => handleChange(toIcd10AnswerData(next));
      $[74] = handleChange;
      $[75] = t16;
    } else {
      t16 = $[75];
    }
    let t17;
    if ($[76] !== disabled || $[77] !== t15 || $[78] !== t16) {
      t17 = <Icd10CodesInput value={t15} onChange={t16} disabled={disabled} />;
      $[76] = disabled;
      $[77] = t15;
      $[78] = t16;
      $[79] = t17;
    } else {
      t17 = $[79];
    }
    let t18;
    if ($[80] !== t14 || $[81] !== t17) {
      t18 = <div className="space-y-2">{t14}{t17}</div>;
      $[80] = t14;
      $[81] = t17;
      $[82] = t18;
    } else {
      t18 = $[82];
    }
    return t18;
  }
  if (isPatientReportedMedicationQuestion(question)) {
    const textData = questionData as Text;
    const t14 = answer.data.valueDetails ?? answer.data.values ?? answer.data.value;
    let t15;
    if ($[83] !== t14) {
      t15 = toPatientReportedMedicationEntries(t14);
      $[83] = t14;
      $[84] = t15;
    } else {
      t15 = $[84];
    }
    const currentMedications = t15;
    let t16;
    if ($[85] !== handleChange) {
      t16 = medications => {
        handleChange({
          value: serializePatientReportedMedications(medications),
          valueDetails: medications
        });
      };
      $[85] = handleChange;
      $[86] = t16;
    } else {
      t16 = $[86];
    }
    const updateMedications = t16;
    const t17 = fullHeight && "flex h-full flex-col";
    let t18;
    if ($[87] !== t17) {
      t18 = cx("space-y-2", t17);
      $[87] = t17;
      $[88] = t18;
    } else {
      t18 = $[88];
    }
    let t19;
    if ($[89] !== questionData.name) {
      t19 = <Label className="flex-shrink-0 text-body-strong">{questionData.name}</Label>;
      $[89] = questionData.name;
      $[90] = t19;
    } else {
      t19 = $[90];
    }
    const t20 = textData.placeholder || "Add medication";
    let t21;
    if ($[91] !== currentMedications || $[92] !== disabled || $[93] !== t20 || $[94] !== updateMedications) {
      t21 = <PatientReportedMedicationInput medications={currentMedications} onChange={updateMedications} disabled={disabled} placeholder={t20} />;
      $[91] = currentMedications;
      $[92] = disabled;
      $[93] = t20;
      $[94] = updateMedications;
      $[95] = t21;
    } else {
      t21 = $[95];
    }
    let t22;
    if ($[96] !== t18 || $[97] !== t19 || $[98] !== t21) {
      t22 = <div className={t18}>{t19}{t21}</div>;
      $[96] = t18;
      $[97] = t19;
      $[98] = t21;
      $[99] = t22;
    } else {
      t22 = $[99];
    }
    return t22;
  }
  let t14;
  bb0: switch (question.questionType) {
    case QuestionType.TEXT:
      {
        const textData_0 = questionData as Text;
        let t15;
        if ($[100] !== questionData.name) {
          t15 = <Label className="flex-shrink-0 text-body-strong">{questionData.name}</Label>;
          $[100] = questionData.name;
          $[101] = t15;
        } else {
          t15 = $[101];
        }
        const t16 = answer.data.value || "";
        let t17;
        if ($[102] !== handleChange) {
          t17 = (text_0, __1, completeText_0) => handleChange({
            value: completeText_0 || text_0 || null
          });
          $[102] = handleChange;
          $[103] = t17;
        } else {
          t17 = $[103];
        }
        const t18 = fullHeight ? "h-full" : "min-h-[40px]";
        const t19 = fullHeight ? "flex flex-1 flex-col [&>div]:flex-1 [&_.ProseMirror]:h-full" : undefined;
        const t20 = !disabled;
        let t21;
        if ($[104] !== handleEditorFocus || $[105] !== suggestions || $[106] !== t16 || $[107] !== t17 || $[108] !== t18 || $[109] !== t19 || $[110] !== t20 || $[111] !== textData_0.placeholder) {
          t21 = <TiptapEditor content={t16} onUpdate={t17} suggestions={suggestions} expandSuggestionsOnInsert={true} richText={true} posthogSurface="note_inline" placeholder={textData_0.placeholder} textArea={false} editorClassName={t18} parentClassName={t19} onEditorReady={setEditor} onFocus={handleEditorFocus} editable={t20} />;
          $[104] = handleEditorFocus;
          $[105] = suggestions;
          $[106] = t16;
          $[107] = t17;
          $[108] = t18;
          $[109] = t19;
          $[110] = t20;
          $[111] = textData_0.placeholder;
          $[112] = t21;
        } else {
          t21 = $[112];
        }
        let t22;
        if ($[113] !== t15 || $[114] !== t21) {
          t22 = <>{t15}{t21}</>;
          $[113] = t15;
          $[114] = t21;
          $[115] = t22;
        } else {
          t22 = $[115];
        }
        t14 = t22;
        break bb0;
      }
    case QuestionType.TEXT_AREA:
      {
        const textAreaData = questionData as Text;
        let t15;
        if ($[116] !== questionData.name) {
          t15 = <Label className="flex-shrink-0 text-body-strong">{questionData.name}</Label>;
          $[116] = questionData.name;
          $[117] = t15;
        } else {
          t15 = $[117];
        }
        const t16 = answer.data.value || "";
        let t17;
        if ($[118] !== handleChange) {
          t17 = (text, __0, completeText) => handleChange({
            value: completeText || text || null
          });
          $[118] = handleChange;
          $[119] = t17;
        } else {
          t17 = $[119];
        }
        const t18 = !fullHeight;
        const t19 = fullHeight ? "h-full" : "min-h-[100px]";
        const t20 = fullHeight ? "flex flex-1 flex-col [&>div]:flex-1 [&_.ProseMirror]:h-full" : undefined;
        const t21 = !disabled;
        let t22;
        if ($[120] !== handleEditorFocus || $[121] !== suggestions || $[122] !== t16 || $[123] !== t17 || $[124] !== t18 || $[125] !== t19 || $[126] !== t20 || $[127] !== t21 || $[128] !== textAreaData.placeholder) {
          t22 = <TiptapEditor content={t16} onUpdate={t17} suggestions={suggestions} expandSuggestionsOnInsert={true} richText={true} posthogSurface="note_inline" placeholder={textAreaData.placeholder} textArea={true} resizable={t18} editorClassName={t19} parentClassName={t20} onEditorReady={setEditor} onFocus={handleEditorFocus} editable={t21} />;
          $[120] = handleEditorFocus;
          $[121] = suggestions;
          $[122] = t16;
          $[123] = t17;
          $[124] = t18;
          $[125] = t19;
          $[126] = t20;
          $[127] = t21;
          $[128] = textAreaData.placeholder;
          $[129] = t22;
        } else {
          t22 = $[129];
        }
        let t23;
        if ($[130] !== t15 || $[131] !== t22) {
          t23 = <>{t15}{t22}</>;
          $[130] = t15;
          $[131] = t22;
          $[132] = t23;
        } else {
          t23 = $[132];
        }
        t14 = t23;
        break bb0;
      }
    case QuestionType.POUNDS_WEIGHT:
      {
        let t15;
        if ($[133] !== answer || $[134] !== disabled || $[135] !== noteId || $[136] !== onChange || $[137] !== patientId || $[138] !== question) {
          t15 = <WeightAnswerEditor answer={answer} question={question} onChange={onChange} disabled={disabled} noteId={noteId} patientId={patientId} />;
          $[133] = answer;
          $[134] = disabled;
          $[135] = noteId;
          $[136] = onChange;
          $[137] = patientId;
          $[138] = question;
          $[139] = t15;
        } else {
          t15 = $[139];
        }
        t14 = t15;
        break bb0;
      }
    case QuestionType.NUMBER:
      {
        let currentValue = answer.data.value;
        if (currentValue === undefined || currentValue === null) {
          currentValue = "";
        }
        let t15;
        if ($[140] !== questionData.name) {
          t15 = <Label className="text-body-strong">{questionData.name}</Label>;
          $[140] = questionData.name;
          $[141] = t15;
        } else {
          t15 = $[141];
        }
        let t16;
        if ($[142] !== handleChange) {
          t16 = e => {
            const value_5 = e.target.value ? parseFloat(e.target.value) : null;
            handleChange({
              value: value_5
            });
          };
          $[142] = handleChange;
          $[143] = t16;
        } else {
          t16 = $[143];
        }
        const t17 = questionData as Number;
        let t18;
        if ($[144] !== currentValue || $[145] !== disabled || $[146] !== t16 || $[147] !== t17.placeholder) {
          t18 = <Input type="number" stripLeadingZeros={false} value={currentValue} onChange={t16} placeholder={t17.placeholder} disabled={disabled} />;
          $[144] = currentValue;
          $[145] = disabled;
          $[146] = t16;
          $[147] = t17.placeholder;
          $[148] = t18;
        } else {
          t18 = $[148];
        }
        let t19;
        if ($[149] !== t15 || $[150] !== t18) {
          t19 = <>{t15}{t18}</>;
          $[149] = t15;
          $[150] = t18;
          $[151] = t19;
        } else {
          t19 = $[151];
        }
        t14 = t19;
        break bb0;
      }
    case QuestionType.FEET_INCHES_HEIGHT:
      {
        let t15;
        if ($[152] === Symbol.for("react.memo_cache_sentinel")) {
          t15 = {};
          $[152] = t15;
        } else {
          t15 = $[152];
        }
        let heightData = t15;
        if (answer.data.value && typeof answer.data.value === "object") {
          heightData = answer.data.value as {
            feet?: number;
            inches?: number;
          };
        } else {
          if ("feet" in answer.data || "inches" in answer.data) {
            heightData = answer.data as {
              feet?: number;
              inches?: number;
            };
          }
        }
        let t16;
        if ($[153] !== questionData.name) {
          t16 = <Label className="text-body-strong">{questionData.name}</Label>;
          $[153] = questionData.name;
          $[154] = t16;
        } else {
          t16 = $[154];
        }
        let t17;
        if ($[155] !== heightData.feet) {
          t17 = heightData.feet?.toString() ?? "";
          $[155] = heightData.feet;
          $[156] = t17;
        } else {
          t17 = $[156];
        }
        let t18;
        if ($[157] !== heightData.inches) {
          t18 = heightData.inches?.toString() ?? "";
          $[157] = heightData.inches;
          $[158] = t18;
        } else {
          t18 = $[158];
        }
        let t19;
        if ($[159] !== t17 || $[160] !== t18) {
          t19 = {
            feet: t17,
            inches: t18
          };
          $[159] = t17;
          $[160] = t18;
          $[161] = t19;
        } else {
          t19 = $[161];
        }
        let t20;
        if ($[162] !== handleChange) {
          t20 = value_4 => handleChange({
            value: {
              feet: value_4.feet ? parseInt(value_4.feet, 10) : 0,
              inches: value_4.inches ? parseFloat(value_4.inches) : 0
            }
          });
          $[162] = handleChange;
          $[163] = t20;
        } else {
          t20 = $[163];
        }
        let t21;
        if ($[164] !== disabled || $[165] !== t19 || $[166] !== t20) {
          t21 = <HeightInput value={t19} onChange={t20} quarterInches={true} disabled={disabled} />;
          $[164] = disabled;
          $[165] = t19;
          $[166] = t20;
          $[167] = t21;
        } else {
          t21 = $[167];
        }
        let t22;
        if ($[168] !== t16 || $[169] !== t21) {
          t22 = <>{t16}{t21}</>;
          $[168] = t16;
          $[169] = t21;
          $[170] = t22;
        } else {
          t22 = $[170];
        }
        t14 = t22;
        break bb0;
      }
    case QuestionType.DATE:
      {
        const t15 = questionData.name;
        const t16 = answer.data.value;
        const t17 = Boolean((questionData as DateQuestion).includeTime);
        const t18 = Boolean((questionData as DateQuestion).showTodayButton);
        let t19;
        if ($[171] !== handleChange) {
          t19 = value_3 => handleChange({
            value: value_3
          });
          $[171] = handleChange;
          $[172] = t19;
        } else {
          t19 = $[172];
        }
        let t20;
        if ($[173] !== answer.data.value || $[174] !== disabled || $[175] !== questionData.name || $[176] !== t17 || $[177] !== t18 || $[178] !== t19) {
          t20 = <DateAnswerField name={t15} value={t16} includeTime={t17} showTodayButton={t18} disabled={disabled} onChange={t19} />;
          $[173] = answer.data.value;
          $[174] = disabled;
          $[175] = questionData.name;
          $[176] = t17;
          $[177] = t18;
          $[178] = t19;
          $[179] = t20;
        } else {
          t20 = $[179];
        }
        t14 = t20;
        break bb0;
      }
    case QuestionType.SINGLE_SELECT:
      {
        const singleSelectData_0 = questionData as SingleSelect;
        let T0;
        let T1;
        let t15;
        let t16;
        let t17;
        let t18;
        let t19;
        let t20;
        if ($[180] !== answer.data.value || $[181] !== disabled || $[182] !== handleChange || $[183] !== question || $[184] !== questionData.name || $[185] !== singleSelectData_0) {
          const showOption_2 = selectOptionDisplay(singleSelectData_0);
          if ($[194] !== questionData.name) {
            t20 = <Label className="text-body-strong">{questionData.name}</Label>;
            $[194] = questionData.name;
            $[195] = t20;
          } else {
            t20 = $[195];
          }
          T1 = Select;
          t17 = answer.data.value || "";
          if ($[196] !== handleChange) {
            t18 = value_2 => handleChange({
              value: value_2
            });
            $[196] = handleChange;
            $[197] = t18;
          } else {
            t18 = $[197];
          }
          if ($[198] === Symbol.for("react.memo_cache_sentinel")) {
            t19 = <SelectTrigger className="max-w-xs" wrapText={true}><SelectValue placeholder="Select an option" /></SelectTrigger>;
            $[198] = t19;
          } else {
            t19 = $[198];
          }
          T0 = SelectContent;
          t15 = true;
          t16 = singleSelectData_0.options?.map(option_3 => <DeselectableSelectItem key={option_3} value={option_3} disabled={disabled} onDeselect={canClearAnswer(question) && option_3 === answer.data.value ? () => handleChange({
            value: null
          }) : undefined}>{showOption_2(option_3)}</DeselectableSelectItem>);
          $[180] = answer.data.value;
          $[181] = disabled;
          $[182] = handleChange;
          $[183] = question;
          $[184] = questionData.name;
          $[185] = singleSelectData_0;
          $[186] = T0;
          $[187] = T1;
          $[188] = t15;
          $[189] = t16;
          $[190] = t17;
          $[191] = t18;
          $[192] = t19;
          $[193] = t20;
        } else {
          T0 = $[186];
          T1 = $[187];
          t15 = $[188];
          t16 = $[189];
          t17 = $[190];
          t18 = $[191];
          t19 = $[192];
          t20 = $[193];
        }
        let t21;
        if ($[199] !== T0 || $[200] !== t15 || $[201] !== t16) {
          t21 = <T0 wrapText={t15}>{t16}</T0>;
          $[199] = T0;
          $[200] = t15;
          $[201] = t16;
          $[202] = t21;
        } else {
          t21 = $[202];
        }
        let t22;
        if ($[203] !== T1 || $[204] !== t17 || $[205] !== t18 || $[206] !== t19 || $[207] !== t21) {
          t22 = <T1 value={t17} onValueChange={t18}>{t19}{t21}</T1>;
          $[203] = T1;
          $[204] = t17;
          $[205] = t18;
          $[206] = t19;
          $[207] = t21;
          $[208] = t22;
        } else {
          t22 = $[208];
        }
        let t23;
        if ($[209] !== t20 || $[210] !== t22) {
          t23 = <>{t20}{t22}</>;
          $[209] = t20;
          $[210] = t22;
          $[211] = t23;
        } else {
          t23 = $[211];
        }
        t14 = t23;
        break bb0;
      }
    case QuestionType.RADIAL_SINGLE_SELECT:
      {
        const singleSelectData = questionData as SingleSelect;
        let T0;
        let t15;
        let t16;
        let t17;
        let t18;
        let t19;
        let t20;
        if ($[212] !== answer.data.value || $[213] !== answer.id || $[214] !== disabled || $[215] !== handleChange || $[216] !== question || $[217] !== questionData.name || $[218] !== singleSelectData) {
          const showOption_1 = selectOptionDisplay(singleSelectData);
          if ($[226] !== questionData.name) {
            t20 = <Label className="text-body-strong">{questionData.name}</Label>;
            $[226] = questionData.name;
            $[227] = t20;
          } else {
            t20 = $[227];
          }
          T0 = RadioGroup;
          t15 = answer.data.value || "";
          if ($[228] !== handleChange) {
            t16 = value_1 => handleChange({
              value: value_1
            });
            $[228] = handleChange;
            $[229] = t16;
          } else {
            t16 = $[229];
          }
          t17 = disabled;
          t18 = "grid grid-cols-[repeat(auto-fit,minmax(10rem,1fr))] gap-x-6 gap-y-2";
          t19 = singleSelectData.options?.map(option_2 => <div key={option_2} className="flex items-center space-x-2"><RadioGroupItem value={option_2} id={`${answer.id}-${option_2}`} onClick={() => {
              if (canClearAnswer(question) && option_2 === answer.data.value) {
                handleChange({
                  value: null
                });
              }
            }} /><Label htmlFor={`${answer.id}-${option_2}`} className="min-w-0 cursor-pointer break-words text-body font-normal">{showOption_1(option_2)}</Label></div>);
          $[212] = answer.data.value;
          $[213] = answer.id;
          $[214] = disabled;
          $[215] = handleChange;
          $[216] = question;
          $[217] = questionData.name;
          $[218] = singleSelectData;
          $[219] = T0;
          $[220] = t15;
          $[221] = t16;
          $[222] = t17;
          $[223] = t18;
          $[224] = t19;
          $[225] = t20;
        } else {
          T0 = $[219];
          t15 = $[220];
          t16 = $[221];
          t17 = $[222];
          t18 = $[223];
          t19 = $[224];
          t20 = $[225];
        }
        let t21;
        if ($[230] !== T0 || $[231] !== t15 || $[232] !== t16 || $[233] !== t17 || $[234] !== t18 || $[235] !== t19) {
          t21 = <T0 value={t15} onValueChange={t16} disabled={t17} className={t18}>{t19}</T0>;
          $[230] = T0;
          $[231] = t15;
          $[232] = t16;
          $[233] = t17;
          $[234] = t18;
          $[235] = t19;
          $[236] = t21;
        } else {
          t21 = $[236];
        }
        let t22;
        if ($[237] !== t20 || $[238] !== t21) {
          t22 = <>{t20}{t21}</>;
          $[237] = t20;
          $[238] = t21;
          $[239] = t22;
        } else {
          t22 = $[239];
        }
        t14 = t22;
        break bb0;
      }
    case QuestionType.MULTI_SELECT:
      {
        const multiSelectData_0 = questionData as MultiSelectType;
        let T0;
        let T1;
        let t15;
        let t16;
        let t17;
        let t18;
        let t19;
        let t20;
        let t21;
        let t22;
        let t23;
        if ($[240] !== answer.data.value || $[241] !== answer.data.values || $[242] !== disabled || $[243] !== handleChange || $[244] !== multiSelectData_0 || $[245] !== questionData.name) {
          const showOption_0 = selectOptionDisplay(multiSelectData_0);
          let t24;
          if ($[257] !== answer.data.value || $[258] !== answer.data.values) {
            t24 = Array.isArray(answer.data.values) ? answer.data.values : answer.data.value ? [answer.data.value] : [];
            $[257] = answer.data.value;
            $[258] = answer.data.values;
            $[259] = t24;
          } else {
            t24 = $[259];
          }
          const currentValues_0 = t24;
          if ($[260] !== questionData.name) {
            t23 = <Label className="text-body-strong">{questionData.name}</Label>;
            $[260] = questionData.name;
            $[261] = t23;
          } else {
            t23 = $[261];
          }
          T1 = MultiSelect;
          t16 = multiSelectData_0.options?.map(option_0 => ({
            value: option_0,
            label: showOption_0(option_0)
          })) || [];
          t17 = currentValues_0;
          if ($[262] !== handleChange) {
            t18 = values => handleChange({
              values
            });
            $[262] = handleChange;
            $[263] = t18;
          } else {
            t18 = $[263];
          }
          t19 = "Select options...";
          t20 = disabled;
          t21 = true;
          if ($[264] === Symbol.for("react.memo_cache_sentinel")) {
            t22 = <MultiSelectTrigger className="max-w-xs" />;
            $[264] = t22;
          } else {
            t22 = $[264];
          }
          T0 = MultiSelectContent;
          t15 = multiSelectData_0.options?.map(option_1 => <MultiSelectItem key={option_1} value={option_1}>{showOption_0(option_1)}</MultiSelectItem>);
          $[240] = answer.data.value;
          $[241] = answer.data.values;
          $[242] = disabled;
          $[243] = handleChange;
          $[244] = multiSelectData_0;
          $[245] = questionData.name;
          $[246] = T0;
          $[247] = T1;
          $[248] = t15;
          $[249] = t16;
          $[250] = t17;
          $[251] = t18;
          $[252] = t19;
          $[253] = t20;
          $[254] = t21;
          $[255] = t22;
          $[256] = t23;
        } else {
          T0 = $[246];
          T1 = $[247];
          t15 = $[248];
          t16 = $[249];
          t17 = $[250];
          t18 = $[251];
          t19 = $[252];
          t20 = $[253];
          t21 = $[254];
          t22 = $[255];
          t23 = $[256];
        }
        let t24;
        if ($[265] !== T0 || $[266] !== t15) {
          t24 = <T0>{t15}</T0>;
          $[265] = T0;
          $[266] = t15;
          $[267] = t24;
        } else {
          t24 = $[267];
        }
        let t25;
        if ($[268] === Symbol.for("react.memo_cache_sentinel")) {
          t25 = <MultiSelectPills className="mt-2" />;
          $[268] = t25;
        } else {
          t25 = $[268];
        }
        let t26;
        if ($[269] !== T1 || $[270] !== t16 || $[271] !== t17 || $[272] !== t18 || $[273] !== t19 || $[274] !== t20 || $[275] !== t21 || $[276] !== t22 || $[277] !== t24) {
          t26 = <T1 options={t16} value={t17} onChange={t18} placeholder={t19} disabled={t20} showOffListValues={t21}>{t22}{t24}{t25}</T1>;
          $[269] = T1;
          $[270] = t16;
          $[271] = t17;
          $[272] = t18;
          $[273] = t19;
          $[274] = t20;
          $[275] = t21;
          $[276] = t22;
          $[277] = t24;
          $[278] = t26;
        } else {
          t26 = $[278];
        }
        let t27;
        if ($[279] !== t23 || $[280] !== t26) {
          t27 = <>{t23}{t26}</>;
          $[279] = t23;
          $[280] = t26;
          $[281] = t27;
        } else {
          t27 = $[281];
        }
        t14 = t27;
        break bb0;
      }
    case QuestionType.RADIAL_MULTI_SELECT:
      {
        const multiSelectData = questionData as MultiSelectType;
        let t15;
        let t16;
        let t17;
        if ($[282] !== answer.data.value || $[283] !== answer.data.values || $[284] !== answer.id || $[285] !== disabled || $[286] !== handleChange || $[287] !== multiSelectData || $[288] !== questionData.name) {
          const showOption = selectOptionDisplay(multiSelectData);
          const currentValues = Array.isArray(answer.data.values) ? answer.data.values : answer.data.value ? [answer.data.value] : [];
          if ($[292] !== questionData.name) {
            t17 = <Label className="text-body-strong">{questionData.name}</Label>;
            $[292] = questionData.name;
            $[293] = t17;
          } else {
            t17 = $[293];
          }
          t15 = "grid grid-cols-[repeat(auto-fit,minmax(10rem,1fr))] gap-x-6 gap-y-2";
          t16 = multiSelectData.options?.map(option => {
            const isSelected = currentValues.includes(option);
            return <div key={option} className="flex items-center space-x-2"><Checkbox id={`${answer.id}-${option}`} checked={isSelected} onCheckedChange={checked_0 => {
                const newValues = checked_0 ? [...currentValues, option] : currentValues.filter(v_2 => v_2 !== option);
                handleChange({
                  values: newValues
                });
              }} disabled={disabled} /><Label htmlFor={`${answer.id}-${option}`} className="min-w-0 cursor-pointer break-words text-body font-normal">{showOption(option)}</Label></div>;
          });
          $[282] = answer.data.value;
          $[283] = answer.data.values;
          $[284] = answer.id;
          $[285] = disabled;
          $[286] = handleChange;
          $[287] = multiSelectData;
          $[288] = questionData.name;
          $[289] = t15;
          $[290] = t16;
          $[291] = t17;
        } else {
          t15 = $[289];
          t16 = $[290];
          t17 = $[291];
        }
        let t18;
        if ($[294] !== t15 || $[295] !== t16) {
          t18 = <div className={t15}>{t16}</div>;
          $[294] = t15;
          $[295] = t16;
          $[296] = t18;
        } else {
          t18 = $[296];
        }
        let t19;
        if ($[297] !== t17 || $[298] !== t18) {
          t19 = <>{t17}{t18}</>;
          $[297] = t17;
          $[298] = t18;
          $[299] = t19;
        } else {
          t19 = $[299];
        }
        t14 = t19;
        break bb0;
      }
    case QuestionType.TAG:
      {
        const tagData = questionData as Tag;
        let t15;
        if ($[300] !== answer.data.value || $[301] !== answer.data.values) {
          t15 = Array.isArray(answer.data.values) ? answer.data.values : answer.data.value ? [answer.data.value] : [];
          $[300] = answer.data.value;
          $[301] = answer.data.values;
          $[302] = t15;
        } else {
          t15 = $[302];
        }
        const currentTags = t15;
        let t16;
        if ($[303] !== questionData.name) {
          t16 = <Label className="text-body-strong">{questionData.name}</Label>;
          $[303] = questionData.name;
          $[304] = t16;
        } else {
          t16 = $[304];
        }
        let t17;
        let t18;
        if ($[305] !== currentTags || $[306] !== handleChange) {
          t17 = tag => handleChange({
            values: [...currentTags, tag]
          });
          t18 = index => handleChange({
            values: currentTags.filter((_, i) => i !== index)
          });
          $[305] = currentTags;
          $[306] = handleChange;
          $[307] = t17;
          $[308] = t18;
        } else {
          t17 = $[307];
          t18 = $[308];
        }
        const t19 = tagData.placeholder || "Add tag";
        let t20;
        if ($[309] !== currentTags || $[310] !== t17 || $[311] !== t18 || $[312] !== t19) {
          t20 = <MultiTagInput tags={currentTags} onAddTag={t17} onRemoveTag={t18} placeholder={t19} />;
          $[309] = currentTags;
          $[310] = t17;
          $[311] = t18;
          $[312] = t19;
          $[313] = t20;
        } else {
          t20 = $[313];
        }
        let t21;
        if ($[314] !== t16 || $[315] !== t20) {
          t21 = <>{t16}{t20}</>;
          $[314] = t16;
          $[315] = t20;
          $[316] = t21;
        } else {
          t21 = $[316];
        }
        t14 = t21;
        break bb0;
      }
    case QuestionType.TOGGLE:
      {
        const toggleValue = answer.data.value;
        let T0;
        let allowDeselect;
        let t15;
        let t16;
        let t17;
        let t18;
        let t19;
        let unanswered;
        if ($[317] !== answer.id || $[318] !== question || $[319] !== toggleValue) {
          allowDeselect = canClearAnswer(question);
          unanswered = allowDeselect && toggleValue !== true && toggleValue !== false;
          t19 = "flex items-center space-x-2";
          T0 = Switch;
          t15 = `answer-${answer.id}`;
          t16 = toggleValue === true;
          t17 = unanswered;
          t18 = cx(unanswered && "bg-muted");
          $[317] = answer.id;
          $[318] = question;
          $[319] = toggleValue;
          $[320] = T0;
          $[321] = allowDeselect;
          $[322] = t15;
          $[323] = t16;
          $[324] = t17;
          $[325] = t18;
          $[326] = t19;
          $[327] = unanswered;
        } else {
          T0 = $[320];
          allowDeselect = $[321];
          t15 = $[322];
          t16 = $[323];
          t17 = $[324];
          t18 = $[325];
          t19 = $[326];
          unanswered = $[327];
        }
        let t20;
        if ($[328] !== allowDeselect || $[329] !== handleChange || $[330] !== toggleValue) {
          t20 = checked => handleChange({
            value: allowDeselect ? nextToggleValue(toggleValue) : checked === true
          });
          $[328] = allowDeselect;
          $[329] = handleChange;
          $[330] = toggleValue;
          $[331] = t20;
        } else {
          t20 = $[331];
        }
        let t21;
        if ($[332] !== T0 || $[333] !== disabled || $[334] !== t15 || $[335] !== t16 || $[336] !== t17 || $[337] !== t18 || $[338] !== t20) {
          t21 = <T0 id={t15} checked={t16} indeterminate={t17} className={t18} onCheckedChange={t20} disabled={disabled} />;
          $[332] = T0;
          $[333] = disabled;
          $[334] = t15;
          $[335] = t16;
          $[336] = t17;
          $[337] = t18;
          $[338] = t20;
          $[339] = t21;
        } else {
          t21 = $[339];
        }
        const t22 = `answer-${answer.id}`;
        let t23;
        if ($[340] !== questionData.name || $[341] !== t22) {
          t23 = <Label htmlFor={t22} className="cursor-pointer text-body-strong">{questionData.name}</Label>;
          $[340] = questionData.name;
          $[341] = t22;
          $[342] = t23;
        } else {
          t23 = $[342];
        }
        let t24;
        if ($[343] !== allowDeselect || $[344] !== toggleValue || $[345] !== unanswered) {
          t24 = allowDeselect && !unanswered && <span className="text-caption text-foreground-muted">{toggleValue ? "Yes" : "No"}</span>;
          $[343] = allowDeselect;
          $[344] = toggleValue;
          $[345] = unanswered;
          $[346] = t24;
        } else {
          t24 = $[346];
        }
        let t25;
        if ($[347] !== t19 || $[348] !== t21 || $[349] !== t23 || $[350] !== t24) {
          t25 = <div className={t19}>{t21}{t23}{t24}</div>;
          $[347] = t19;
          $[348] = t21;
          $[349] = t23;
          $[350] = t24;
          $[351] = t25;
        } else {
          t25 = $[351];
        }
        t14 = t25;
        break bb0;
      }
    case QuestionType.SCRIBE_ATTACHMENTS:
      {
        if (!noteId || !patientId) {
          let t15;
          if ($[352] !== questionData.name) {
            t15 = <Label className="text-body-strong">{questionData.name}</Label>;
            $[352] = questionData.name;
            $[353] = t15;
          } else {
            t15 = $[353];
          }
          let t16;
          if ($[354] === Symbol.for("react.memo_cache_sentinel")) {
            t16 = <p className="text-caption text-foreground-secondary">Note must be saved before uploading files</p>;
            $[354] = t16;
          } else {
            t16 = $[354];
          }
          let t17;
          if ($[355] !== t15) {
            t17 = <>{t15}{t16}</>;
            $[355] = t15;
            $[356] = t17;
          } else {
            t17 = $[356];
          }
          t14 = t17;
          break bb0;
        }
        let t15;
        if ($[357] !== question.required) {
          t15 = question.required && <span className="ml-1 text-destructive">*</span>;
          $[357] = question.required;
          $[358] = t15;
        } else {
          t15 = $[358];
        }
        let t16;
        if ($[359] !== questionData.name || $[360] !== t15) {
          t16 = <Label className="text-body-strong">{questionData.name}{t15}</Label>;
          $[359] = questionData.name;
          $[360] = t15;
          $[361] = t16;
        } else {
          t16 = $[361];
        }
        let t17;
        if ($[362] !== disabled) {
          t17 = <NoteAttachmentSection disabled={disabled} />;
          $[362] = disabled;
          $[363] = t17;
        } else {
          t17 = $[363];
        }
        let t18;
        if ($[364] !== t16 || $[365] !== t17) {
          t18 = <>{t16}{t17}</>;
          $[364] = t16;
          $[365] = t17;
          $[366] = t18;
        } else {
          t18 = $[366];
        }
        t14 = t18;
        break bb0;
      }
    case QuestionType.DENTAL_CHART:
      {
        const cfg_1 = questionData as DentalChartConfig;
        let t15;
        if ($[367] !== answer.data?.value) {
          t15 = answer.data?.value as DentalChartAnswer ?? {
            teeth: {}
          };
          $[367] = answer.data?.value;
          $[368] = t15;
        } else {
          t15 = $[368];
        }
        const current_1 = t15;
        let t16;
        if ($[369] !== cfg_1.name) {
          t16 = <Label className="text-body-strong">{cfg_1.name}</Label>;
          $[369] = cfg_1.name;
          $[370] = t16;
        } else {
          t16 = $[370];
        }
        let t17;
        if ($[371] !== handleChange) {
          t17 = v_1 => handleChange({
            value: v_1
          });
          $[371] = handleChange;
          $[372] = t17;
        } else {
          t17 = $[372];
        }
        let t18;
        if ($[373] !== cfg_1 || $[374] !== current_1 || $[375] !== disabled || $[376] !== patient || $[377] !== t17 || $[378] !== visitDate) {
          t18 = <DentalChartComponent questionData={cfg_1} value={current_1} onChange={t17} disabled={disabled} patient={patient} visitDate={visitDate} />;
          $[373] = cfg_1;
          $[374] = current_1;
          $[375] = disabled;
          $[376] = patient;
          $[377] = t17;
          $[378] = visitDate;
          $[379] = t18;
        } else {
          t18 = $[379];
        }
        let t19;
        if ($[380] !== t16 || $[381] !== t18) {
          t19 = <>{t16}{t18}</>;
          $[380] = t16;
          $[381] = t18;
          $[382] = t19;
        } else {
          t19 = $[382];
        }
        t14 = t19;
        break bb0;
      }
    case QuestionType.PERIO_CHART:
      {
        const cfg_0 = questionData as PerioChartConfig;
        let t15;
        if ($[383] !== answer.data?.value) {
          t15 = answer.data?.value as PerioChartAnswer ?? {
            teeth: {}
          };
          $[383] = answer.data?.value;
          $[384] = t15;
        } else {
          t15 = $[384];
        }
        const current_0 = t15;
        let t16;
        if ($[385] !== cfg_0.name) {
          t16 = <Label className="text-body-strong">{cfg_0.name}</Label>;
          $[385] = cfg_0.name;
          $[386] = t16;
        } else {
          t16 = $[386];
        }
        let t17;
        if ($[387] !== handleChange) {
          t17 = v_0 => handleChange({
            value: v_0
          });
          $[387] = handleChange;
          $[388] = t17;
        } else {
          t17 = $[388];
        }
        let t18;
        if ($[389] !== cfg_0 || $[390] !== current_0 || $[391] !== disabled || $[392] !== t17) {
          t18 = <PerioChartComponent questionData={cfg_0} value={current_0} onChange={t17} disabled={disabled} />;
          $[389] = cfg_0;
          $[390] = current_0;
          $[391] = disabled;
          $[392] = t17;
          $[393] = t18;
        } else {
          t18 = $[393];
        }
        let t19;
        if ($[394] !== t16 || $[395] !== t18) {
          t19 = <>{t16}{t18}</>;
          $[394] = t16;
          $[395] = t18;
          $[396] = t19;
        } else {
          t19 = $[396];
        }
        t14 = t19;
        break bb0;
      }
    case QuestionType.OCCLUSION_EXAM:
      {
        const cfg = questionData as OcclusionExamConfig;
        let t15;
        if ($[397] !== answer.data?.value) {
          t15 = answer.data?.value as OcclusionExamAnswer ?? {};
          $[397] = answer.data?.value;
          $[398] = t15;
        } else {
          t15 = $[398];
        }
        const current = t15;
        let t16;
        if ($[399] !== cfg.name) {
          t16 = <Label className="text-body-strong">{cfg.name}</Label>;
          $[399] = cfg.name;
          $[400] = t16;
        } else {
          t16 = $[400];
        }
        let t17;
        if ($[401] !== handleChange) {
          t17 = v => handleChange({
            value: v
          });
          $[401] = handleChange;
          $[402] = t17;
        } else {
          t17 = $[402];
        }
        let t18;
        if ($[403] !== cfg || $[404] !== current || $[405] !== disabled || $[406] !== t17) {
          t18 = <OcclusionExamComponent questionData={cfg} value={current} onChange={t17} disabled={disabled} />;
          $[403] = cfg;
          $[404] = current;
          $[405] = disabled;
          $[406] = t17;
          $[407] = t18;
        } else {
          t18 = $[407];
        }
        let t19;
        if ($[408] !== t16 || $[409] !== t18) {
          t19 = <>{t16}{t18}</>;
          $[408] = t16;
          $[409] = t18;
          $[410] = t19;
        } else {
          t19 = $[410];
        }
        t14 = t19;
        break bb0;
      }
    case QuestionType.FILE_UPLOAD:
      {
        if (!noteId) {
          let t15;
          if ($[411] !== questionData.name) {
            t15 = <Label className="text-body-strong">{questionData.name}</Label>;
            $[411] = questionData.name;
            $[412] = t15;
          } else {
            t15 = $[412];
          }
          let t16;
          if ($[413] === Symbol.for("react.memo_cache_sentinel")) {
            t16 = <p className="text-caption text-foreground-secondary">Note must be saved before uploading documents</p>;
            $[413] = t16;
          } else {
            t16 = $[413];
          }
          let t17;
          if ($[414] !== t15) {
            t17 = <>{t15}{t16}</>;
            $[414] = t15;
            $[415] = t17;
          } else {
            t17 = $[415];
          }
          t14 = t17;
          break bb0;
        }
        let t15;
        if ($[416] !== question.required || $[417] !== questionData.name) {
          t15 = <Label className="text-body-strong" mandatory={question.required}>{questionData.name}</Label>;
          $[416] = question.required;
          $[417] = questionData.name;
          $[418] = t15;
        } else {
          t15 = $[418];
        }
        let t16;
        if ($[419] !== disabled || $[420] !== noteId || $[421] !== t15) {
          t16 = <NoteDocumentsSection noteId={noteId} disabled={disabled} label={t15} />;
          $[419] = disabled;
          $[420] = noteId;
          $[421] = t15;
          $[422] = t16;
        } else {
          t16 = $[422];
        }
        t14 = t16;
        break bb0;
      }
    case QuestionType.SIGNATURE:
      {
        const sigData = questionData as SignatureConfig;
        let t15;
        if ($[423] !== questionData.name) {
          t15 = <Label className="text-body-strong">{questionData.name}</Label>;
          $[423] = questionData.name;
          $[424] = t15;
        } else {
          t15 = $[424];
        }
        let t16;
        if ($[425] !== sigData.text) {
          t16 = sigData.text && <p className="text-caption text-foreground-secondary">{sigData.text}</p>;
          $[425] = sigData.text;
          $[426] = t16;
        } else {
          t16 = $[426];
        }
        const t17 = typeof answer.data.value === "string" ? answer.data.value : null;
        let t18;
        if ($[427] !== handleChange) {
          t18 = value_0 => handleChange({
            value: value_0
          });
          $[427] = handleChange;
          $[428] = t18;
        } else {
          t18 = $[428];
        }
        let t19;
        if ($[429] !== disabled || $[430] !== t17 || $[431] !== t18) {
          t19 = <SignatureInput value={t17} onChange={t18} disabled={disabled} />;
          $[429] = disabled;
          $[430] = t17;
          $[431] = t18;
          $[432] = t19;
        } else {
          t19 = $[432];
        }
        let t20;
        if ($[433] !== t15 || $[434] !== t16 || $[435] !== t19) {
          t20 = <>{t15}{t16}{t19}</>;
          $[433] = t15;
          $[434] = t16;
          $[435] = t19;
          $[436] = t20;
        } else {
          t20 = $[436];
        }
        t14 = t20;
        break bb0;
      }
    case QuestionType.PROVIDER_SIGNATURE:
      {
        const psData = questionData as ProviderSignatureConfig;
        let t15;
        if ($[437] !== questionData.name) {
          t15 = <Label className="text-body-strong">{questionData.name}</Label>;
          $[437] = questionData.name;
          $[438] = t15;
        } else {
          t15 = $[438];
        }
        let t16;
        if ($[439] !== psData.text) {
          t16 = psData.text && <p className="text-caption text-foreground-secondary">{psData.text}</p>;
          $[439] = psData.text;
          $[440] = t16;
        } else {
          t16 = $[440];
        }
        let t17;
        if ($[441] !== handleChange) {
          t17 = value => handleChange(value);
          $[441] = handleChange;
          $[442] = t17;
        } else {
          t17 = $[442];
        }
        const t18 = currentUser?.id;
        let t19;
        if ($[443] !== answer.data || $[444] !== disabled || $[445] !== t17 || $[446] !== t18) {
          t19 = <ProviderSignatureInput value={answer.data} onChange={t17} disabled={disabled} defaultProviderId={t18} />;
          $[443] = answer.data;
          $[444] = disabled;
          $[445] = t17;
          $[446] = t18;
          $[447] = t19;
        } else {
          t19 = $[447];
        }
        let t20;
        if ($[448] !== t15 || $[449] !== t16 || $[450] !== t19) {
          t20 = <>{t15}{t16}{t19}</>;
          $[448] = t15;
          $[449] = t16;
          $[450] = t19;
          $[451] = t20;
        } else {
          t20 = $[451];
        }
        t14 = t20;
        break bb0;
      }
    case QuestionType.DISCLAIMER:
      {
        const t15 = questionData as Disclaimer;
        let t16;
        if ($[452] !== t15) {
          t16 = <DisclaimerDisplay data={t15} />;
          $[452] = t15;
          $[453] = t16;
        } else {
          t16 = $[453];
        }
        t14 = t16;
        break bb0;
      }
    case QuestionType.STATEMENT:
      {
        const t15 = questionData as Statement;
        let t16;
        if ($[454] !== t15) {
          t16 = <StatementDisplay data={t15} />;
          $[454] = t15;
          $[455] = t16;
        } else {
          t16 = $[455];
        }
        t14 = t16;
        break bb0;
      }
    default:
      {
        let t15;
        if ($[456] !== questionData.name) {
          t15 = <Label className="text-body-strong">{questionData.name}</Label>;
          $[456] = questionData.name;
          $[457] = t15;
        } else {
          t15 = $[457];
        }
        let t16;
        if ($[458] !== question.questionType) {
          t16 = <div className="text-body text-foreground-secondary">Unsupported question type: {question.questionType}</div>;
          $[458] = question.questionType;
          $[459] = t16;
        } else {
          t16 = $[459];
        }
        let t17;
        if ($[460] !== t15 || $[461] !== t16) {
          t17 = <>{t15}{t16}</>;
          $[460] = t15;
          $[461] = t16;
          $[462] = t17;
        } else {
          t17 = $[462];
        }
        t14 = t17;
      }
  }
  const fieldContent = t14;
  let t15;
  if ($[463] !== showPatientReportedPrefill) {
    t15 = showPatientReportedPrefill ? <div className="flex items-center"><MeasurementSourceBadge isUserReported={true} /></div> : null;
    $[463] = showPatientReportedPrefill;
    $[464] = t15;
  } else {
    t15 = $[464];
  }
  const patientReportedPrefillBadge = t15;
  if (question.questionType === QuestionType.TOGGLE) {
    let t16;
    if ($[465] !== fieldContent || $[466] !== patientReportedPrefillBadge) {
      t16 = patientReportedPrefillBadge ? <div className="space-y-1">{fieldContent}{patientReportedPrefillBadge}</div> : fieldContent;
      $[465] = fieldContent;
      $[466] = patientReportedPrefillBadge;
      $[467] = t16;
    } else {
      t16 = $[467];
    }
    return t16;
  }
  const isAttachmentsWithContext = question.questionType === QuestionType.SCRIBE_ATTACHMENTS && noteId && patientId;
  const isDocumentUploadWithNote = question.questionType === QuestionType.FILE_UPLOAD && noteId;
  const spacingClass = isAttachmentsWithContext || isDocumentUploadWithNote ? "space-y-4" : "";
  const t16 = fullHeight ? "flex flex-1 flex-col space-y-1" : "space-y-1";
  const t17 = spacingClass || undefined;
  let t18;
  if ($[468] !== t16 || $[469] !== t17) {
    t18 = cx(t16, t17);
    $[468] = t16;
    $[469] = t17;
    $[470] = t18;
  } else {
    t18 = $[470];
  }
  let t19;
  if ($[471] !== fieldContent || $[472] !== patientReportedPrefillBadge || $[473] !== t18) {
    t19 = <div className={t18}>{fieldContent}{patientReportedPrefillBadge}</div>;
    $[471] = fieldContent;
    $[472] = patientReportedPrefillBadge;
    $[473] = t18;
    $[474] = t19;
  } else {
    t19 = $[474];
  }
  return t19;
};

// Drawn-signature input backed by react-signature-canvas. Stores the signature
// as a data-URL string in answer.data.value. When disabled (read-only), shows
// the saved signature image instead of the canvas.
const SignatureInput: React.FC<{
  value: string | null;
  onChange: (value: string | null) => void;
  disabled?: boolean;
}> = t0 => {
  const $ = _c(18);
  const {
    value,
    onChange,
    disabled: t1
  } = t0;
  const disabled = t1 === undefined ? false : t1;
  const sigCanvas = useRef(null);
  let t2;
  let t3;
  if ($[0] !== disabled || $[1] !== value) {
    t2 = () => {
      if (disabled) {
        return;
      }
      const canvas = sigCanvas.current;
      if (!canvas) {
        return;
      }
      if (value) {
        canvas.fromDataURL(value);
      } else {
        if (!canvas.isEmpty()) {
          canvas.clear();
        }
      }
    };
    t3 = [value, disabled];
    $[0] = disabled;
    $[1] = value;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  let t4;
  if ($[4] !== onChange) {
    t4 = () => {
      const canvas_0 = sigCanvas.current;
      if (!canvas_0) {
        return;
      }
      onChange(canvas_0.isEmpty() ? null : canvas_0.toDataURL());
    };
    $[4] = onChange;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const save = t4;
  let t5;
  if ($[6] !== onChange) {
    t5 = () => {
      sigCanvas.current?.clear();
      onChange(null);
    };
    $[6] = onChange;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const clear = t5;
  if (disabled) {
    let t6;
    if ($[8] !== value) {
      t6 = value ? <img src={value} alt="Signature" className="max-h-40 rounded border border-border bg-background" /> : <div className="text-body text-foreground-muted">Not signed</div>;
      $[8] = value;
      $[9] = t6;
    } else {
      t6 = $[9];
    }
    return t6;
  }
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      className: "w-full h-40"
    };
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== save) {
    t7 = <div className="rounded border border-border bg-background p-2"><SignatureCanvas ref={sigCanvas} canvasProps={t6} clearOnResize={false} onEnd={save} /></div>;
    $[11] = save;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== clear) {
    t8 = <Button type="button" variant="secondary" size="sm" onClick={clear}>Clear</Button>;
    $[13] = clear;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t7 || $[16] !== t8) {
    t9 = <div className="space-y-2">{t7}{t8}</div>;
    $[15] = t7;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  return t9;
};

// DISCLAIMER and STATEMENT are display-only — they render their configured
// content and never collect an answer.
const DisclaimerDisplay: React.FC<{
  data: Disclaimer;
}> = t0 => {
  const $ = _c(7);
  const {
    data
  } = t0;
  let t1;
  if ($[0] !== data.name) {
    t1 = data.name && <p className="mb-1 text-body-strong text-foreground">{data.name}</p>;
    $[0] = data.name;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== data.content) {
    t2 = data.content && <p className="whitespace-pre-wrap text-body text-foreground-secondary">{data.content}</p>;
    $[2] = data.content;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="rounded-lg border border-border bg-background-secondary/40 p-3">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};
const StatementDisplay: React.FC<{
  data: Statement;
}> = t0 => {
  const $ = _c(7);
  const {
    data
  } = t0;
  let t1;
  if ($[0] !== data.name) {
    t1 = data.name && <p className="mb-1 text-body-strong text-foreground">{data.name}</p>;
    $[0] = data.name;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== data.points) {
    t2 = data.points && data.points.length > 0 && <ul className="list-disc space-y-1 pl-5 text-body text-foreground-secondary">{data.points.map(_temp)}</ul>;
    $[2] = data.points;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <div className="rounded-lg border border-border bg-background-secondary/40 p-3">{t1}{t2}</div>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};
function _temp(point, index) {
  return <li key={index}>{point}</li>;
}